import { useRef, type ElementType } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { DUR, EASE, STAGGER, MQ } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger, useGSAP);

interface SplitTextProps {
  text: string;
  as?: ElementType;
  className?: string;
  /** Words from this index onward render in the accent blue. */
  highlightFrom?: number;
  stagger?: number;
  delay?: number;
  start?: string;
}

/**
 * Word-by-word mask reveal — the move that defines this style of site.
 *
 * Each word sits in an overflow-hidden wrapper and slides up from below, so
 * words appear to rise out of the line rather than simply fading.
 *
 * `highlightFrom` colours the tail of the headline in accent blue, reproducing
 * the two-tone tagline from the client's banner (white line / blue line).
 *
 * Accessibility: the heading carries the full string as aria-label and the
 * per-word spans are aria-hidden, so a screen reader announces one sentence
 * rather than a stream of fragments.
 */
export function SplitText({
  text,
  as: Tag = 'h2',
  className = '',
  highlightFrom,
  stagger = STAGGER.word,
  delay = 0,
  /*
    Fires as the block enters the viewport, not 82% down it. Display type set
    over several lines is tall, so with the later trigger a statement could sit
    half on screen and still be blank — its top edge had not reached the line
    yet. START is still the default for the shorter reveals.
  */
  start = 'top 96%',
}: SplitTextProps) {
  const ref = useRef<HTMLElement>(null);
  const words = text.split(' ');

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const targets = el.querySelectorAll<HTMLElement>('[data-word]');
      const mm = gsap.matchMedia();

      mm.add(MQ.motion, () => {
        gsap.set(targets, { yPercent: 115 });

        /*
          Elements already in view on load must animate immediately rather than
          wait for a scroll trigger.

          This is what made hero headings render blank: a hero starts at the top
          of the viewport, so its trigger point is already behind the start
          position on load. ScrollTrigger treated it as passed and — with
          once: true — never fired, leaving every word masked at yPercent 115
          with nothing visible.
        */
        const box = el.getBoundingClientRect();
        const alreadyVisible = box.top < window.innerHeight * 0.9;

        const tween = {
          yPercent: 0,
          duration: DUR.slow,
          delay,
          ease: EASE.out,
          stagger,
        };

        if (alreadyVisible) {
          gsap.to(targets, tween);
        } else {
          gsap.to(targets, {
            ...tween,
            scrollTrigger: { trigger: el, start, once: true },
          });
        }
      });

      mm.add(MQ.reduced, () => {
        gsap.set(targets, { yPercent: 0 });
      });

      /*
        Revert the matchMedia context, then explicitly clear the masked state.

        React 18 StrictMode mounts, unmounts and remounts in development. The
        revert on that first unmount restores the pre-animation state — words
        parked at yPercent 115 — and the remount could leave them there, so
        every headline rendered blank in dev while the production build was
        fine. Clearing the transform on teardown guarantees text is never left
        hidden, whatever the mount sequence.
      */
      return () => {
        mm.revert();
        gsap.set(targets, { yPercent: 0, clearProps: 'transform' });
      };
    },
    { scope: ref },
  );

  return (
    <Tag ref={ref} className={className} aria-label={text}>
      {words.map((word, i) => (
        <span
          key={`${word}-${i}`}
          aria-hidden="true"
          // The mask. Bottom padding stops descenders (g, y, p) being clipped.
          className="inline-block overflow-hidden pb-[0.12em] align-bottom"
        >
          <span
            data-word
            className="inline-block"
            style={highlightFrom !== undefined && i >= highlightFrom ? { color: 'var(--color-brand-accent)' } : undefined}
          >
            {word}
            {i < words.length - 1 ? ' ' : ''}
          </span>
        </span>
      ))}
    </Tag>
  );
}
